import type {DefaultError} from "@tanstack/react-query";
import type {UrlQueryKey} from "~/query/queryClient.ts";
import {useCustomQuery} from "~/query/queryClient.ts";
import type {ContractCheck, ContractOpportunity, ContractOpportunityDetail, OpportunityKind} from "./contractExplorerTypes.ts";
import {contractDetailUrl} from "./useContractDetailQuery.ts";

/** `GET /api/data/{databag_id}/contracts/{id}/opportunities/` */
export const contractOpportunitiesUrl = (bagId: number, contractId: number) =>
    `${contractDetailUrl(bagId, contractId)}opportunities/`;

const enabled = (bagId: number, contractId: number) => bagId > 0 && contractId > 0;

/** The bundle's opportunities, the most at stake first; of one kind when given. Not paged: tens per bundle. */
export function useContractOpportunitiesQuery(bagId: number, contractId: number, kind: OpportunityKind | null) {
    return useCustomQuery<ContractOpportunity[], DefaultError, ContractOpportunity[], UrlQueryKey>({
        queryKey: [contractOpportunitiesUrl(bagId, contractId), {params: kind ? {kind} : {}}],
        enabled: enabled(bagId, contractId),
        staleTime: 5 * 60_000,
    });
}

/** One opportunity with the transactions it stands on. */
export function useContractOpportunityQuery(bagId: number, contractId: number, opportunityId: number | null) {
    return useCustomQuery<ContractOpportunityDetail, DefaultError, ContractOpportunityDetail, UrlQueryKey>({
        queryKey: [`${contractOpportunitiesUrl(bagId, contractId)}${opportunityId}/`],
        enabled: enabled(bagId, contractId) && opportunityId !== null,
        staleTime: 5 * 60_000,
    });
}

/** What each specialist made of the bundle. */
export function useContractChecksQuery(bagId: number, contractId: number) {
    return useCustomQuery<ContractCheck[], DefaultError, ContractCheck[], UrlQueryKey>({
        queryKey: [`${contractOpportunitiesUrl(bagId, contractId)}checks/`],
        enabled: enabled(bagId, contractId),
        staleTime: 5 * 60_000,
    });
}
